<template>
  <v-dialog
    v-model="dialog"
    max-width="500"
    persistent
  >
    <v-card>
      <v-card-title class="text-h5 d-flex">
        <span v-if="mode === 'stake'">Fazer Stake de Tokens LP</span>
        <span v-else-if="mode === 'unstake'">Remover Stake de Tokens LP</span>
        <span v-else>Detalhe do Staking</span>
        <v-spacer></v-spacer>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="closeDialog"
        ></v-btn>
      </v-card-title>

      <v-card-text class="pt-4">
        <!-- Cabeçalho da pool -->
        <div class="d-flex align-center mb-4">
          <div class="token-pair-icons position-relative mr-2">
            <v-avatar size="32" :color="stakingPool?.pool.token1.color" class="token-icon">
              <span class="text-caption text-white">{{ stakingPool?.pool.token1.symbol.charAt(0) }}</span>
            </v-avatar>
            <v-avatar size="32" :color="stakingPool?.pool.token2.color" class="token-icon token-icon-second">
              <span class="text-caption text-white">{{ stakingPool?.pool.token2.symbol.charAt(0) }}</span>
            </v-avatar>
          </div>
          <div>
            <div class="text-h6">{{ stakingPool?.pool.token1.symbol }}/{{ stakingPool?.pool.token2.symbol }}</div>
            <div class="text-caption">{{ getNetworkName(stakingPool?.network) }} &bull; {{ getStakingTypeLabel(stakingPool?.type) }}</div>
          </div>
        </div>

        <!-- Informações importantes -->
        <v-row class="mb-4">
          <v-col cols="6">
            <div class="text-caption text-medium-emphasis mb-1">APR</div>
            <div class="text-subtitle-1 font-weight-medium">{{ formatNumber(stakingPool?.apr) }}%</div>
            <div class="text-caption">
              Base: {{ formatNumber(stakingPool?.baseApr) }}% + 
              Taxas: {{ formatNumber(stakingPool?.feeApr) }}%
            </div>
          </v-col>
          <v-col cols="6">
            <div class="text-caption text-medium-emphasis mb-1">Duração</div>
            <div class="text-subtitle-1 font-weight-medium">{{ formatDuration(stakingPool?.duration) }}</div>
            <div v-if="stakingPool?.endDate" class="text-caption">
              Até {{ formatDate(stakingPool?.endDate) }}
            </div>
          </v-col>
        </v-row>

        <v-divider class="mb-4"></v-divider>

        <!-- Formulário de stake/unstake -->
        <template v-if="mode === 'stake' || mode === 'unstake'">
          <div class="mb-4">
            <div class="d-flex justify-space-between align-center mb-1">
              <span class="text-caption text-medium-emphasis">
                {{ mode === 'stake' ? 'Tokens LP disponíveis' : 'Tokens LP em staking' }}
              </span>
              <span class="text-caption">
                {{ formatNumber(mode === 'stake' ? availableLpTokens : userStake?.stakedAmount) }}
              </span>
            </div>
            
            <v-text-field
              v-model="inputAmount"
              type="number"
              label="Quantidade para stake"
              variant="outlined"
              density="compact"
              hide-details
              class="mb-2"
              @input="validateAmount"
              :hint="mode === 'stake' ? `Mínimo: ${stakingPool?.minStake} tokens LP` : ''"
              :error-messages="inputError"
              :suffix="'LP'"
            ></v-text-field>
            
            <v-slider
              v-model="sliderValue"
              :max="100"
              :min="0"
              :step="1"
              thumb-label
              class="mt-2"
              @update:modelValue="updateInputFromSlider"
            ></v-slider>
            
            <div class="d-flex justify-space-between">
              <v-btn 
                size="small" 
                variant="text" 
                @click="setPercentage(25)"
                class="slider-btn"
              >
                25%
              </v-btn>
              <v-btn 
                size="small" 
                variant="text" 
                @click="setPercentage(50)"
                class="slider-btn"
              >
                50%
              </v-btn>
              <v-btn 
                size="small" 
                variant="text" 
                @click="setPercentage(75)"
                class="slider-btn"
              >
                75%
              </v-btn>
              <v-btn 
                size="small" 
                variant="text" 
                @click="setPercentage(100)"
                class="slider-btn"
              >
                Max
              </v-btn>
            </div>
          </div>

          <v-divider class="mb-4"></v-divider>

          <!-- Resumo da operação -->
          <div class="mb-6">
            <div class="text-subtitle-1 font-weight-medium mb-3">Resumo</div>
            
            <div class="d-flex justify-space-between mb-2">
              <span class="text-body-2">Valor estimado:</span>
              <span class="text-body-2">${{ formatNumber(estimatedValue) }}</span>
            </div>
            
            <div class="d-flex justify-space-between mb-2">
              <span class="text-body-2">Recompensa estimada (diária):</span>
              <span class="text-body-2">{{ formatNumber(estimatedDailyReward) }} {{ coin }}</span>
            </div>
            
            <div class="d-flex justify-space-between mb-2">
              <span class="text-body-2">Recompensa estimada (mensal):</span>
              <span class="text-body-2">{{ formatNumber(estimatedMonthlyReward) }} {{ coin }}</span>
            </div>
            
            <div v-if="stakingPool?.type === 'locked'" class="d-flex justify-space-between">
              <span class="text-body-2">Data de desbloqueio:</span>
              <span class="text-body-2">{{ formatDate(estimatedUnlockDate) }}</span>
            </div>
          </div>
        </template>

        <!-- Visualização dos detalhes do staking atual -->
        <template v-else-if="mode === 'info' && userStake">
          <div class="mb-4">
            <div class="d-flex justify-space-between mb-2">
              <span class="text-body-2">Tokens LP em staking:</span>
              <span class="text-body-2">{{ formatNumber(userStake.stakedAmount) }}</span>
            </div>
            
            <div class="d-flex justify-space-between mb-2">
              <span class="text-body-2">Valor em stake:</span>
              <span class="text-body-2">${{ formatNumber(userStake.stakedValue) }}</span>
            </div>
            
            <div class="d-flex justify-space-between mb-2">
              <span class="text-body-2">Recompensas pendentes:</span>
              <span class="text-body-2 font-weight-bold">{{ formatNumber(userStake.rewards) }} {{ coin }}</span>
            </div>
            
            <div class="d-flex justify-space-between mb-2">
              <span class="text-body-2">Data de início:</span>
              <span class="text-body-2">{{ formatDate(userStake.startDate) }}</span>
            </div>
            
            <div v-if="userStake.unlockDate" class="d-flex justify-space-between mb-2">
              <span class="text-body-2">Desbloqueio em:</span>
              <span class="text-body-2">{{ formatDate(userStake.unlockDate) }}</span>
            </div>
            
            <div class="d-flex justify-space-between">
              <span class="text-body-2">Status:</span>
              <v-chip
                :color="getStatusColor(userStake.status)"
                size="x-small"
                class="text-uppercase"
              >
                {{ getStatusLabel(userStake.status) }}
              </v-chip>
            </div>
          </div>
          
          <v-card color="grey-lighten-4" class="mb-4 pa-3">
            <div class="text-subtitle-2 mb-2">Estimativa de ganhos</div>
            <div class="d-flex justify-space-between mb-1">
              <span class="text-caption">Diário:</span>
              <span class="text-caption">{{ formatNumber(currentDailyReward) }} {{ coin }}</span>
            </div>
            <div class="d-flex justify-space-between mb-1">
              <span class="text-caption">Semanal:</span>
              <span class="text-caption">{{ formatNumber(currentDailyReward * 7) }} {{ coin }}</span>
            </div>
            <div class="d-flex justify-space-between">
              <span class="text-caption">Mensal:</span>
              <span class="text-caption">{{ formatNumber(currentDailyReward * 30) }} {{ coin }}</span>
            </div>
          </v-card>
        </template>
      </v-card-text>

      <v-card-actions class="pb-4 px-4">
        <template v-if="mode === 'stake'">
          <v-btn
            block
            color="primary"
            :disabled="!isValidInput || inputAmount <= 0"
            @click="confirmStake"
          >
            Fazer Stake
          </v-btn>
        </template>
        
        <template v-else-if="mode === 'unstake'">
          <v-btn
            block
            color="error"
            :disabled="!isValidInput || inputAmount <= 0"
            @click="confirmUnstake"
          >
            Remover Stake
          </v-btn>
        </template>
        
        <template v-else-if="mode === 'info'">
          <v-row>
            <v-col cols="6">
              <v-btn
                block
                color="primary"
                variant="outlined"
                @click="switchToUnstake"
                :disabled="userStake?.status !== 'active' || (stakingPool?.type === 'locked' && new Date() < userStake?.unlockDate)"
              >
                Remover Stake
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                block
                color="success"
                @click="claimRewards"
                :disabled="userStake?.rewards <= 0"
              >
                Reclamar {{ formatNumber(userStake?.rewards) }} {{ coin }}
              </v-btn>
            </v-col>
          </v-row>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { useAppStore } from '@/stores/app'

export default {
  name: 'LpStakingDialog',
  
  props: {
    show: Boolean,
    stakingPool: Object,
    userStake: Object,
    mode: {
      type: String,
      default: 'info', // 'info', 'stake', 'unstake'
      validator: (value) => ['info', 'stake', 'unstake'].includes(value)
    }
  },
  
  data() {
    return {
      dialog: false,
      inputAmount: 0,
      sliderValue: 0,
      inputError: '',
      
      // Mock data
      availableLpTokens: 1.75, // Mock de tokens LP disponíveis para stake
    }
  },
  
  computed: {
    appStore() {
      return useAppStore()
    },
    
    isValidInput() {
      return !this.inputError && this.inputAmount > 0
    },
    
    estimatedValue() {
      // Cálculo do valor estimado baseado na quantia de stake
      if (!this.stakingPool || !this.inputAmount) return 0
      
      // Valor por token LP baseado no TVL total e tokens em circulação
      const lpValue = 100 // Valor hipotético
      
      return this.inputAmount * lpValue
    },
    
    estimatedDailyReward() {
      // Calcula recompensa diária baseada na APR
      if (!this.stakingPool || !this.inputAmount) return 0
      
      const dailyRate = this.stakingPool.apr / 365 / 100
      return this.estimatedValue * dailyRate
    },
    
    estimatedMonthlyReward() {
      return this.estimatedDailyReward * 30
    },
    
    estimatedUnlockDate() {
      if (!this.stakingPool || this.stakingPool.type !== 'locked') return null
      
      const durationDays = this.stakingPool.duration / (24 * 60 * 60)
      const unlockDate = new Date()
      unlockDate.setDate(unlockDate.getDate() + durationDays)
      
      return unlockDate
    },
    
    currentDailyReward() {
      if (!this.userStake || !this.stakingPool) return 0
      
      const dailyRate = this.stakingPool.apr / 365 / 100
      return this.userStake.stakedValue * dailyRate
    }
  },
  
  watch: {
    show(val) {
      this.dialog = val
    },
    
    dialog(val) {
      if (!val) {
        this.$emit('update:show', false)
      }
    },
    
    stakingPool() {
      this.resetForm()
    },
    
    mode() {
      this.resetForm()
    }
  },
  
  methods: {
    closeDialog() {
      this.dialog = false
    },
    
    resetForm() {
      this.inputAmount = 0
      this.sliderValue = 0
      this.inputError = ''
    },
    
    validateAmount() {
      this.inputError = ''
      
      if (this.mode === 'stake') {
        // Validações para stake
        if (this.inputAmount <= 0) {
          this.inputError = 'A quantidade deve ser maior que zero'
        } else if (this.inputAmount > this.availableLpTokens) {
          this.inputError = 'Quantidade maior que o saldo disponível'
        } else if (this.stakingPool && this.inputAmount < this.stakingPool.minStake) {
          this.inputError = `Mínimo de ${this.stakingPool.minStake} tokens LP`
        }
      } else if (this.mode === 'unstake') {
        // Validações para unstake
        if (this.inputAmount <= 0) {
          this.inputError = 'A quantidade deve ser maior que zero'
        } else if (this.userStake && this.inputAmount > this.userStake.stakedAmount) {
          this.inputError = 'Quantidade maior que o stake atual'
        }
      }
      
      // Atualiza slider para refletir a entrada
      this.updateSliderFromInput()
    },
    
    updateSliderFromInput() {
      const max = this.mode === 'stake' ? this.availableLpTokens : (this.userStake?.stakedAmount || 0)
      if (max <= 0) {
        this.sliderValue = 0
        return
      }
      
      this.sliderValue = Math.min(Math.max((this.inputAmount / max) * 100, 0), 100)
    },
    
    updateInputFromSlider() {
      const max = this.mode === 'stake' ? this.availableLpTokens : (this.userStake?.stakedAmount || 0)
      this.inputAmount = parseFloat(((max * this.sliderValue) / 100).toFixed(6))
      this.validateAmount()
    },
    
    setPercentage(percentage) {
      this.sliderValue = percentage
      this.updateInputFromSlider()
    },
    
    confirmStake() {
      // Processamento do stake
      this.appStore.showSnackbar({
        text: `Stake de ${this.formatNumber(this.inputAmount)} tokens LP confirmado`,
        color: 'success'
      })
      
      // Emitir evento com detalhes da operação
      this.$emit('stake-confirmed', {
        stakingPoolId: this.stakingPool.id,
        amount: this.inputAmount,
        value: this.estimatedValue
      })
      
      this.closeDialog()
    },
    
    confirmUnstake() {
      // Processamento do unstake
      this.appStore.showSnackbar({
        text: `Remoção de ${this.formatNumber(this.inputAmount)} tokens LP confirmada`,
        color: 'success'
      })
      
      // Emitir evento com detalhes da operação
      this.$emit('unstake-confirmed', {
        stakeId: this.userStake.id,
        amount: this.inputAmount
      })
      
      this.closeDialog()
    },
    
    switchToUnstake() {
      this.$emit('change-mode', 'unstake')
    },
    
    claimRewards() {
      if (!this.userStake || this.userStake.rewards <= 0) return
      
      this.appStore.showSnackbar({
        text: `Reclamando ${this.formatNumber(this.userStake.rewards)} ${window.__QUARNX_COIN__ || 'tKinX'} de recompensas...`,
        color: 'success'
      })
      
      // Emitir evento para processar o claim
      this.$emit('claim-rewards', {
        stakeId: this.userStake.id,
        amount: this.userStake.rewards
      })
      
      this.closeDialog()
    },
    
    formatNumber(value) {
      if (value === null || value === undefined) return '0.00'
      return parseFloat(value).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    
    formatDate(date) {
      if (!date) return '—'
      return new Date(date).toLocaleDateString('pt-BR')
    },
    
    formatDuration(seconds) {
      if (!seconds) return 'Flexível'
      if (seconds === 0) return 'Flexível'
      
      const days = Math.floor(seconds / (24 * 60 * 60))
      
      if (days % 30 === 0) {
        const months = days / 30
        return months === 1 ? '1 mês' : `${months} meses`
      } else {
        return days === 1 ? '1 dia' : `${days} dias`
      }
    },
    
    getNetworkName(networkId) {
      const networks = {
        'quarnx': 'QuarnX',
        'ethereum': 'Ethereum',
        'bsc': 'BSC',
        'polkadot': 'Polkadot'
      }
      return networks[networkId] || 'Desconhecida'
    },
    
    getStakingTypeLabel(type) {
      const types = {
        'flexible': 'Flexível',
        'locked': 'Bloqueado'
      }
      return types[type] || type
    },
    
    getStatusLabel(status) {
      const statuses = {
        'active': 'Ativo',
        'ended': 'Encerrado',
        'pending': 'Pendente'
      }
      return statuses[status] || status
    },
    
    getStatusColor(status) {
      const colors = {
        'active': 'success',
        'ended': 'grey',
        'pending': 'warning'
      }
      return colors[status] || 'grey'
    }
  }
}
</script>

<style scoped>
.token-icon {
  z-index: 1;
}

.token-icon-second {
  margin-left: -12px;
}

.slider-btn {
  opacity: 0.7;
}

.slider-btn:hover {
  opacity: 1;
}
</style>
